import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add,
  AdminPanelSettings,
  CheckCircle,
  Close,
  Edit,
  ManageAccounts,
  Refresh,
  RestartAlt,
  Search,
  Security,
  Block,
  Visibility,
  Diversity1,
} from "@mui/icons-material";

import PageHeader from "../../utils/PageHeader";

import {
  getUsers,
  createUser,
  updateUser,
  updateUserStatus,
  resetUserPassword,
} from "../../services/userManagementService";

import { buildRightsFromMenu } from "../../utils/buildRights";

// ============================================================
// IMPORTANT
// ============================================================
// Change this import to your actual menuConfig location.
// ============================================================

import menuConfig from "../../config/menuConfig";

// ============================================================
// EMPTY FORM
// ============================================================

const emptyForm = {
  id: null,
  loginUserName: "",
  loginName: "",
  email: "",
  mobile: "",
  depart: "",
  loginRights: [],
};

// ============================================================
// COMPONENT
// ============================================================

export default function UserManagement({ profile }) {
  // ========================================================
  // STATE
  // ========================================================

const token = sessionStorage.getItem("token");

const getTokenDepartment = () => {
  if (!token) return "";

  try {
    const payload = JSON.parse(
      atob(token.split(".")[1])
    );

    return String(payload.depart || "")
      .trim()
      .toUpperCase();
  } catch (error) {
    console.error("Invalid JWT:", error);
    return "";
  }
};

const userDepart = getTokenDepartment();

const isSuperUser = userDepart === "S";



  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [searchInput, setSearchInput] = useState("");

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [totalRows, setTotalRows] = useState(0);

  const [formOpen, setFormOpen] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState(emptyForm);

  const [selectedUser, setSelectedUser] = useState(null);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const [statusTarget, setStatusTarget] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ========================================================
  // ALL RIGHTS
  // ========================================================

  const availableRights = useMemo(() => {
    return buildRightsFromMenu(menuConfig);
  }, []);

  // ========================================================
  // CURRENT USER RIGHTS
  // ========================================================
let depart = "";
  const currentUserRights = useMemo(() => {
    try {
      let session = null;

      // 1. Try sessionStorage
      const sessionStorageKeys = [
        "session",
        "userSession",
        "user",
        "profile",
        "authUser",
      ];

      for (const key of sessionStorageKeys) {
        const value = sessionStorage.getItem(key);

        if (value) {
          try {
            session = JSON.parse(value);

          //   if (session?.role || session?.loginRights) {
          //     console.log(
          //       `User session found in sessionStorage["${key}"]`,
          //       session,
          //     );
          //     break;
          //   }
           } catch {
             // Ignore invalid JSON
           }
        }
      }

      // 2. Try localStorage if not found
      if (!session) {
        const localStorageKeys = [
          "session",
          "userSession",
          "user",
          "profile",
          "authUser",
        ];

        for (const key of localStorageKeys) {
          const value = localStorage.getItem(key);

          if (value) {
            try {
              session = JSON.parse(value);

              // if (session?.role || session?.loginRights) {
              //   console.log(
              //     `User session found in localStorage["${key}"]`,
              //     session,
              //   );
               // break;
             // }
            } catch {
              // Ignore invalid JSON
            }
          }
        }
      }

      if (!session) {
        console.error("No authenticated user session found");

        return [];
      }

      const rightsValue = session.role ?? session.loginRights ?? "";

      const rights = String(rightsValue)
        .split(";")
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean);

     // console.log("Authenticated user:", session);

    //  console.log("Authenticated user rights:", rights);

      return rights;
    } catch (error) {
      console.error("Permission check error:", error);

      return [];
    }
  });

  const canManageUsers =
    currentUserRights.includes("Q") || currentUserRights.includes("ADMIN");

  //console.log("User Management permission:", canManageUsers);

  // // ========================================================
  // // USER MANAGEMENT PERMISSION
  // // ========================================================
  // // Change "U" if your menuConfig uses another code.
  // //
  // // Better: use your actual User Management permission code.
  // // ========================================================

  // const canManageUsers =
  //     currentUserRights.includes("Q") ||
  //     currentUserRights.includes("ADMIN");

  ///console.log("Current User Rights:", currentUserRights);

  // ============
  // ============================================
  // LOAD USERS
  // ========================================================

  const loadUsers = async () => {
    try {
      setLoading(true);

      const response = await getUsers({
        search,
        page: page + 1,
        limit: rowsPerPage,
      });

      const responseData = response?.data;

      setUsers(responseData?.data || []);

      setTotalRows(Number(responseData?.pagination?.total || 0));
    } catch (error) {
      console.error("Load users error:", error);

      setUsers([]);

      showMessage(
        error?.response?.data?.message || "Unable to load users",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // ========================================================
  // INITIAL LOAD
  // ========================================================

  useEffect(() => {
    loadUsers();
  }, [search, page, rowsPerPage]);

  // ========================================================
  // MESSAGE
  // ========================================================

  const showMessage = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // ========================================================
  // SEARCH
  // ========================================================

  const handleSearch = () => {
    setPage(0);

    setSearch(searchInput.trim());
  };

  // ========================================================
  // SEARCH ENTER
  // ========================================================

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();

      handleSearch();
    }
  };

  // ========================================================
  // CLEAR SEARCH
  // ========================================================

  const handleClearSearch = () => {
    setSearchInput("");

    setSearch("");

    setPage(0);
  };

  // ========================================================
  // OPEN CREATE
  // ========================================================

  const openCreate = () => {
  setEditMode(false);

  setForm({
    ...emptyForm,
    depart:
      userDepart === "A"
        ? "A"
        : userDepart === "T"
          ? "T"
          : userDepart === "S"
            ? "A"
            : "",
  });

  setFormOpen(true);
};

  // ========================================================
  // OPEN EDIT
  // ========================================================

 const openEdit = (user) => {
  const rights = String(user.loginRights || "")
    .split(";")
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);

 const finalRights = Array.from(new Set(rights));
  setEditMode(true);

  setForm({
    id: user.id,
    loginUserName: user.loginUserName || "",
    loginName: user.loginName || "",
    email: user["E-Mail"] || "",
    mobile: user.mobile || "",
    depart: String(user.depart || "")
      .trim()
      .toUpperCase(),
    loginRights: finalRights,
  });

  setFormOpen(true);
};

  // ========================================================
  // FORM CHANGE
  // ========================================================

  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // ========================================================
  // TOGGLE RIGHT
  // ========================================================

  const toggleRight = (code) => {
  const normalizedCode = String(code).trim().toUpperCase();

  // O can never be removed
  // if (normalizedCode === "O") {
  //   return;
  // }

  setForm((previous) => {
    const currentRights = Array.isArray(previous.loginRights)
      ? previous.loginRights
          .map((item) => String(item).trim().toUpperCase())
          .filter(Boolean)
      : [];

    const hasRight = currentRights.includes(normalizedCode);

    const updatedRights = hasRight
      ? currentRights.filter((item) => item !== normalizedCode)
      : [...currentRights, normalizedCode];

    // Always keep O
    // if (!updatedRights.includes("O")) {
    //   updatedRights.push("O");
    // }

    return {
      ...previous,
      loginRights: updatedRights,
    };
  });
};
  // ========================================================
  // SELECT ALL RIGHTS
  // ========================================================

  const selectAllRights = () => {
  setForm((previous) => ({
    ...previous,
   loginRights: Array.from(
  new Set(
    availableRights.map((item) =>
      String(item.code).trim().toUpperCase()
    )
  )
),
  }));
};

  // ========================================================
  // CLEAR ALL RIGHTS
  // ========================================================

  const clearAllRights = () => {
  setForm((previous) => ({
    ...previous,
    loginRights: [],
  }));
};
  // ========================================================
  // SAVE
  // ========================================================

  const handleSave = async () => {
    if (!form.loginUserName.trim()) {
      showMessage("Username is required", "error");

      return;
    }

    if (!form.loginName.trim()) {
      showMessage("User name is required", "error");

      return;
    }

    if (!form.email.trim()) {
      showMessage("Email is required", "error");

      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(form.email.trim())) {
      showMessage("Please enter a valid email address", "error");

      return;
    }

    try {
      setSaving(true);


      const finalDepart =
  userDepart === "A"
    ? "A"
    : userDepart === "T"
      ? "T"
      : form.depart;


const finalRights = Array.from(
  new Set(
    (Array.isArray(form.loginRights) ? form.loginRights : [])
      .map((item) => String(item).trim().toUpperCase())
      .filter(Boolean)
  )
);

//console.log("FORM RIGHTS:", form.loginRights);
//console.log("FINAL RIGHTS:", finalRights);

const payload = {
  loginUserName: form.loginUserName.trim(),
  loginName: form.loginName.trim(),
  email: form.email.trim(),
  mobile: form.mobile.trim(),
  loginRights: finalRights.join(";"),
  depart: finalDepart,
};





//console.log("Payload for save:", payload);


      if (editMode) {
        const response = await updateUser(form.id, payload);

        showMessage(response?.data?.message || "User updated successfully");
      } else {
        const response = await createUser(payload);

        showMessage(response?.data?.message || "User created successfully");
      }

      setFormOpen(false);

      setForm({
        ...emptyForm,
      });

      await loadUsers();
    } catch (error) {
      console.error("Save user error:", error);

      showMessage(
        error?.response?.data?.message || "Unable to save user",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // ========================================================
  // OPEN RESET
  // ========================================================

  const openResetPassword = (user) => {
    setSelectedUser(user);

    setResetDialogOpen(true);
  };

  // ========================================================
  // RESET PASSWORD
  // ========================================================

  const handleResetPassword = async () => {
    if (!selectedUser) {
      return;
    }

    try {
      setSaving(true);

      const response = await resetUserPassword(selectedUser.id);

      showMessage(response?.data?.message || "Password reset successfully");

      setResetDialogOpen(false);

      setSelectedUser(null);

      await loadUsers();
    } catch (error) {
      console.error("Reset password error:", error);

      showMessage(
        error?.response?.data?.message || "Unable to reset password",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // ========================================================
  // OPEN STATUS DIALOG
  // ========================================================

  const openStatusDialog = (user) => {
    setStatusTarget(user);

    setStatusDialogOpen(true);
  };

  // ========================================================
  // CHANGE STATUS
  // ========================================================

  const handleStatusChange = async () => {
    if (!statusTarget) {
      return;
    }

    const currentStatus = Number(statusTarget.activestatus) === 1;

    const newStatus = !currentStatus;

    try {
      setSaving(true);

      const response = await updateUserStatus(statusTarget.id, newStatus);

      showMessage(response?.data?.message || "User status updated");

      setStatusDialogOpen(false);

      setStatusTarget(null);

      await loadUsers();
    } catch (error) {
      console.error("Status error:", error);

      showMessage(
        error?.response?.data?.message || "Unable to update user status",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // ========================================================
  // RIGHTS DISPLAY
  // ========================================================

  const getRightDetails = (rightsString) => {
    const codes = String(rightsString || "")
      .split(";")
      .map((item) => item.trim())
      .filter(Boolean);

    return codes.map((code) => {
      const found = availableRights.find((item) => item.code === code);

      return {
        code,
        titles: found?.titles || [],
      };
    });
  };

  // ========================================================
  // IF NO MANAGEMENT PERMISSION
  // ========================================================

  if (!canManageUsers) {
    return (
      <Box
        sx={{
          p: {
            xs: 2,
            md: 3,
          },
        }}
      >
        <PageHeader
          title="User Management"
          subtitle="Manage application users and access rights"
          icon={ManageAccounts}
        />

        <Alert
          severity="error"
          sx={{
            mt: 2,
          }}
        >
          You do not have permission to access User Management.
        </Alert>
      </Box>
    );
  }

  // ========================================================
  // UI
  // ========================================================

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        boxSizing: "border-box",
        backgroundColor: "#f5f7fa",
        p: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >
      {/* ==================================================
                PAGE HEADER
            ================================================== */}

      <PageHeader
        title="User Management"
        subtitle="Manage application users, access rights and account security"
        icon={ManageAccounts}
        statusChip={{
          label: loading ? "Loading..." : `${totalRows} Users`,
          color: loading ? "warning" : totalRows > 0 ? "primary" : "default",
        }}
        showRefresh
        onRefresh={loadUsers}
      />

      {/* ==================================================
                MAIN CARD
            ================================================== */}

      <Card
        elevation={3}
        sx={{
          mt: 2,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 1.5,
              sm: 2,
              md: 2.5,
            },
          }}
        >
          {/* ==================================================
                        TOOLBAR
                    ================================================== */}

          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{
              xs: "stretch",
              md: "center",
            }}
            sx={{
              mb: 2,
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search username, name, email or mobile..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: {
                  md: 600,
                },
              }}
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {search && (
                <Button variant="outlined" onClick={handleClearSearch}>
                  Clear
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadUsers}
              >
                Refresh
              </Button>

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openCreate}
                sx={{
                  background:
                    "linear-gradient(135deg,#0d47a1 0%,#1976d2 55%,#42a5f5 100%)",
                }}
              >
                Create User
              </Button>
            </Stack>
          </Stack>

          {/* ==================================================
                        SUMMARY
                    ================================================== */}

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            sx={{
              mb: 2,
            }}
          >
            <Chip
              icon={<ManageAccounts />}
              label={`Total: ${totalRows}`}
              color="primary"
              variant="outlined"
            />

            <Chip
              icon={<CheckCircle />}
              label={`Active: ${
                users.filter((user) => Number(user.activestatus) === 1).length
              }`}
              color="success"
              variant="outlined"
            />

            <Chip
              icon={<Block />}
              label={`Inactive: ${
                users.filter((user) => Number(user.activestatus) !== 1).length
              }`}
              color="default"
              variant="outlined"
            />
          </Stack>

          {/* ==================================================
                        TABLE
                    ================================================== */}

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              maxHeight: 650,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>

                  <TableCell>User</TableCell>

                  <TableCell>Username</TableCell>

                  <TableCell>Email</TableCell>

                  <TableCell>Mobile</TableCell>

                   

                  <TableCell>Rights</TableCell>

                  <TableCell>Department</TableCell>

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={{
                        py: 5,
                      }}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={{
                        py: 5,
                      }}
                    >
                      <Typography color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, index) => {
                    const active = Number(user.activestatus) === 1;

                    const rights = getRightDetails(user.loginRights);

                    return (
                      <TableRow key={user.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(135deg,#0d47a1,#42a5f5)",
                                color: "#fff",
                                fontWeight: 700,
                              }}
                            >
                              {String(user.loginName || "U")
                                .charAt(0)
                                .toUpperCase()}
                            </Box>

                            <Typography fontWeight={600}>
                              {user.loginName}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography fontWeight={600}>
                            {user.loginUserName}
                          </Typography>
                        </TableCell>

                        <TableCell>{user["E-Mail"]}</TableCell>

                        <TableCell>{user.mobile || "-"}</TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {rights.slice(0, 5).map((right) => (
                              <Tooltip
                                key={right.code}
                                title={right.titles.join(", ")}
                              >
                                <Chip
                                  size="small"
                                  label={right.code}
                                  color="primary"
                                  variant="outlined"
                                />
                              </Tooltip>
                            ))}

                            {rights.length > 5 && (
                              <Chip
                                size="small"
                                label={`+${rights.length - 5}`}
                              />
                            )}
                          </Stack>
                        </TableCell>


                  <TableCell>
  {user.depart === "A"
    ? "Accounts"
    : user.depart === "T"
      ? "Workshop"
      : ""}
</TableCell>



                        <TableCell>
                          <Chip
                            size="small"
                            icon={active ? <CheckCircle /> : <Block />}
                            label={active ? "Active" : "Inactive"}
                            color={active ? "success" : "default"}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Stack direction="row" justifyContent="center">
                            <Tooltip title="Edit User">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => openEdit(user)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Reset Password">
                              <IconButton
                                color="warning"
                                size="small"
                                onClick={() => openResetPassword(user)}
                              >
                                <RestartAlt />
                              </IconButton>
                            </Tooltip>

                            <Tooltip
                              title={
                                active ? "Deactivate User" : "Activate User"
                              }
                            >
                              <IconButton
                                color={active ? "error" : "success"}
                                size="small"
                                onClick={() => openStatusDialog(user)}
                              >
                                {active ? <Block /> : <CheckCircle />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ==================================================
                        PAGINATION
                    ================================================== */}

          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));

              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      {/* ========================================================
                CREATE / EDIT DIALOG
            ======================================================== */}

      <Dialog
        open={formOpen}
        onClose={() => {
          if (!saving) {
            setFormOpen(false);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            background:
              "linear-gradient(135deg,#0d47a1 0%,#1976d2 55%,#42a5f5 100%)",
            color: "#fff",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <ManageAccounts />

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {editMode ? "Edit User" : "Create New User"}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.9,
                  }}
                >
                  {editMode
                    ? "Update account details and access rights"
                    : "Create an account and email temporary login credentials"}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              sx={{
                color: "#fff",
              }}
              onClick={() => setFormOpen(false)}
              disabled={saving}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Grid
            container
            spacing={2}
            sx={{
              mt: 0.5,
            }}
          >
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={form.loginUserName}
                onChange={(event) =>
                  handleChange("loginUserName", event.target.value)
                }
                disabled={saving || editMode}
                helperText={
                  editMode ? "Username cannot be changed" : "Login username"
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="User Name"
                value={form.loginName}
                onChange={(event) =>
                  handleChange("loginName", event.target.value)
                }
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={form.email}
                onChange={(event) => handleChange("email", event.target.value)}
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={form.mobile}
                onChange={(event) => handleChange("mobile", event.target.value)}
                disabled={saving}
              />
            </Grid>


            <Grid item xs={12} md={6}>
  <TextField
    select
    fullWidth
    label="Department"
    value={form.depart}
    onChange={(event) =>
      handleChange("depart", event.target.value)
    }
    disabled={saving || !isSuperUser}
    helperText={
      userDepart === "A"
        ? "Department fixed to Account"
        : userDepart === "T"
          ? "Department fixed to Time Office"
          : userDepart === "S"
            ? "Super User can select the department"
            : "Department not available"
    }
  >
    <MenuItem value="A">
      Account
    </MenuItem>

    <MenuItem value="T">
      Time Office
    </MenuItem>
  </TextField>
</Grid>

          </Grid>

          <Divider
            sx={{
              my: 3,
            }}
          />

          {/* ==================================================
                        RIGHTS HEADER
                    ================================================== */}

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            justifyContent="space-between"
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
            spacing={1}
            sx={{
              mb: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Security color="primary" />

              <Box>
                <Typography fontWeight={700}>Application Rights</Typography>

                <Typography variant="caption" color="text.secondary">
                  Select the modules this user can access
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={selectAllRights}>
                Select All
              </Button>

              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={clearAllRights}
              >
                Clear All
              </Button>
            </Stack>
          </Stack>

          {/* ==================================================
                        RIGHTS GRID
                    ================================================== */}

          {availableRights
  .filter((right) => {
    const code = String(right.code || "")
      .trim()
      .toUpperCase();

    if (userDepart === "T") {
      return ["O", "Q", "E"].includes(code);
    }

    if (userDepart === "A") {
      return ["F", "G", "H", "K", "I", "J","O","Q"].includes(code);
    }

    if (userDepart === "S") {
      return true;
    }

    return false;
  }).map((right) => {
  //const isMandatory = right.code === "O";
  const selected = form.loginRights.includes(
  String(right.code).trim().toUpperCase()
);
  return (
    <div
  item
  xs={12}   // Mobile: 1 column
  md={4}    // Desktop: 3 columns
  key={right.code}
  sx={{
    display: "flex",
    minWidth: 0,
  }}
>
      <Paper
        variant="outlined"
        sx={{
         // width: "100%",
          minHeight:50,
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
            mb: 1,
          border: "1px solid",
          borderColor: selected ? "primary.main" : "#d9dfe7",

          backgroundColor: selected
            ? "rgba(25, 118, 210, 0.06)"
            : "#ffffff",

          boxShadow: selected
            ? "0 2px 8px rgba(25,118,210,0.12)"
            : "0 1px 3px rgba(0,0,0,0.05)",

          transition: "all 0.2s ease",

          "&:hover": {
            borderColor: "primary.main",
            boxShadow: "0 4px 12px rgba(25,118,210,0.15)",
            transform: "translateY(-1px)",
          },
        }}
      >
        {/* TOP ROW */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mb: 1,
           // width: "100%",
          }}
        >
          <Checkbox
  checked={selected}
  onChange={() => toggleRight(right.code)}
  disabled={saving}
            size="medium"
            sx={{
              p: 0.5,
              color: "#777",

              "&.Mui-checked": {
                color: "primary.main",
              },
            }}
          />

          <Chip
            label={right.code}
            size="small"
            color={selected ? "primary" : "default"}
            sx={{
              height: 28,
              minWidth: 28,
              fontWeight: 700,

              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />

           <Typography
            variant="body2"
            sx={{
              fontWeight: selected ? 600 : 500,
              color: selected ? "primary.dark" : "text.primary",
              lineHeight: 1.4,
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              marginLeft: 5,
            }}
          >
            {right.titles.join(", ")}
          </Typography>
        </Box>

        
      </Paper>
    </div>
  );
})}

          {/* ==================================================
                        NEW USER INFORMATION
                    ================================================== */}

          {!editMode && (
            <Alert
              severity="info"
              icon={<Security />}
              sx={{
                mt: 2,
              }}
            >
              A secure temporary password will be automatically generated and
              sent to the user's registered email address. The password will not
              be displayed or stored as plain text.
            </Alert>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setFormOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : editMode ? (
                <Edit />
              ) : (
                <Add />
              )
            }
            sx={{
              minWidth: 150,
              background:
                "linear-gradient(135deg,#0d47a1 0%,#1976d2 55%,#42a5f5 100%)",
            }}
          >
            {saving ? "Saving..." : editMode ? "Update User" : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
                RESET PASSWORD DIALOG
            ======================================================== */}

      <Dialog
        open={resetDialogOpen}
        onClose={() => {
          if (!saving) {
            setResetDialogOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <RestartAlt color="warning" />

            <Typography variant="h6" fontWeight={700}>
              Reset User Password
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
            }}
          >
            A new temporary password will be generated and sent to the user's
            registered email address.
          </Alert>

          {selectedUser && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
              }}
            >
              <Typography>
                <strong>User:</strong> {selectedUser.loginName}
              </Typography>

              <Typography>
                <strong>Username:</strong> {selectedUser.loginUserName}
              </Typography>

              <Typography>
                <strong>Email:</strong> {selectedUser["E-Mail"]}
              </Typography>
            </Paper>
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 2,
            }}
          >
            The existing password will immediately stop working after the reset.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setResetDialogOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="warning"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <RestartAlt />
              )
            }
            onClick={handleResetPassword}
            disabled={saving}
          >
            {saving ? "Resetting..." : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
                STATUS DIALOG
            ======================================================== */}

      <Dialog
        open={statusDialogOpen}
        onClose={() => {
          if (!saving) {
            setStatusDialogOpen(false);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {Number(statusTarget?.activestatus) === 1
            ? "Deactivate User"
            : "Activate User"}
        </DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to{" "}
            <strong>
              {Number(statusTarget?.activestatus) === 1
                ? "deactivate"
                : "activate"}
            </strong>{" "}
            this user?
          </Typography>

          {statusTarget && (
            <Typography
              sx={{
                mt: 1,
              }}
            >
              User: <strong>{statusTarget.loginName}</strong>
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color={
              Number(statusTarget?.activestatus) === 1 ? "error" : "success"
            }
            onClick={handleStatusChange}
            disabled={saving}
          >
            {saving
              ? "Processing..."
              : Number(statusTarget?.activestatus) === 1
                ? "Deactivate"
                : "Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
                SNACKBAR
            ======================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() =>
          setSnackbar((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((previous) => ({
              ...previous,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
